// Lease content (markdown components + print CSS) for the browser-side renderer
// (leaseRenderer.ts), which compiles the live HTML preview without a per-render
// API round-trip.
//
// This intentionally diverges from the server-side renderer (lease_render/
// render_lease.py) — the user accepted that preview-vs-PDF drift is fine for
// the live preview. The PDF, which IS the document the tenant signs, still goes
// through the Python renderer + WeasyPrint via /api/lease/pdf.
//
// === Bundled-as-initial, latest-from-runtime ===
//
// The content is bundled into the SPA at build time (below) so the preview ALWAYS
// has a working copy with zero network dependency. That bundled copy is used as
// the INITIAL value of a mutable in-memory store. `readComponent` / `getLeaseCss`
// read from that store.
//
// The server PDF renderer, by contrast, pulls content fresh from S3 at request
// time, so the bundled SPA copy drifts behind deployed content. To keep the
// preview faithful, call `loadLatestLeaseContent()` once (e.g. where the preview
// mounts): it fetches GET /api/lease-content/bundle and, on success, replaces the
// store with the server's current content. On ANY failure (network, 401, parse)
// it silently keeps the bundled copy — the preview must never break because the
// fetch failed.

import { api } from '@/lib/api';

// Vite glob: read every .md file in /lease_components at build time and
// inline its content as a string. The keys are paths relative to this file.
const componentRaw = import.meta.glob<string>(
  '../../../../lease_components/*.md',
  { eager: true, query: '?raw', import: 'default' }
);

const bundledComponentMap: Record<string, string> = {};
for (const [path, content] of Object.entries(componentRaw)) {
  const filename = path.split('/').pop()!;
  bundledComponentMap[filename] = content;
}

import leaseCssRaw from '../../../../lease_render/styles/whp_lease.css?raw';
import watermarkCssRaw from '../../../../lease_render/styles/watermark.css?raw';

const bundledCss = leaseCssRaw + '\n' + watermarkCssRaw;

// ---------------------------------------------------------------------------
// In-memory store. Initialized to the bundled content; replaced wholesale by
// loadLatestLeaseContent() on a successful fetch.
// ---------------------------------------------------------------------------

interface LeaseContentStore {
  css: string;
  components: Record<string, string>;
}

let store: LeaseContentStore = {
  css: bundledCss,
  // Copy so a later store-replacement never mutates the bundled baseline.
  components: { ...bundledComponentMap },
};

/** The combined print CSS (whp_lease.css + watermark.css) for the preview. */
export function getLeaseCss(): string {
  return store.css;
}

/**
 * Read a lease component by filename (e.g., "core_lease.md") and return
 * the body after the `## Content` marker, matching the server-side renderer.
 */
export function readComponent(filename: string): string {
  const text = store.components[filename];
  if (text === undefined) {
    throw new Error(`Unknown lease component: ${filename}`);
  }
  const marker = '\n## Content\n';
  const idx = text.indexOf(marker);
  if (idx === -1) {
    throw new Error(`${filename} does not contain a ## Content section`);
  }
  return text.slice(idx + marker.length).trim();
}

export function hasComponent(filename: string): boolean {
  return filename in store.components;
}

// ---------------------------------------------------------------------------
// Runtime fetch of the latest server-side content.
// ---------------------------------------------------------------------------

interface LeaseContentBundleResponse {
  css: string;
  components: Record<string, string>;
}

// Memoize so concurrent / repeat callers share one in-flight request and we
// never re-fetch after the first attempt (success OR failure → keep whatever
// the store currently holds, which is at minimum the bundled copy).
let loadPromise: Promise<void> | null = null;

/**
 * Fetch the latest lease content (GET /api/lease-content/bundle) and replace the
 * in-memory store on success. Memoized: subsequent calls return the same promise.
 *
 * CRITICAL: this NEVER rejects and NEVER leaves the store in a broken state. On
 * any failure (network error, 401, malformed payload) it logs a warning and
 * keeps the existing (bundled) content, so the preview keeps working.
 */
export function loadLatestLeaseContent(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const bundle = await api.get<LeaseContentBundleResponse>(
        '/api/lease-content/bundle',
      );
      // Validate the shape defensively before swapping the store. A malformed
      // payload must not blank out the preview.
      if (
        bundle &&
        typeof bundle.css === 'string' &&
        bundle.components &&
        typeof bundle.components === 'object'
      ) {
        store = {
          css: bundle.css,
          components: { ...bundle.components },
        };
      } else {
        console.warn(
          'loadLatestLeaseContent: unexpected payload shape; keeping bundled content',
        );
      }
    } catch (err) {
      // Network failure, 401, etc. — keep the bundled content. The preview is
      // unaffected; it just shows the build-time copy.
      console.warn('loadLatestLeaseContent: fetch failed; keeping bundled content', err);
    }
  })();
  return loadPromise;
}
